import { useEffect, useState } from "react";
import { ShieldAlert } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { apiError } from "@/lib/api";

export function PasswordPrompt({ open, onClose, onVerified, description, submitLabel = "Unlock delete mode" }) {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setPw(""); setErr(""); } }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/verify-edit", { password: pw });
      onVerified();
    } catch (ex) { setErr(apiError(ex)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm rounded-sm" data-testid="delete-password-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base font-medium">
            <ShieldAlert className="h-4 w-4 text-rose-600" strokeWidth={1.5} /> Confirm delete access
          </DialogTitle>
          <DialogDescription className="sr-only">Verify the admin password to enter delete mode.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <p className="text-xs text-zinc-500">{description || "Enter the admin password to enable deletion."}</p>
          <input data-testid="delete-password-input" type="password" autoFocus value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Admin password"
            className="block w-full rounded-sm border border-zinc-300 px-3 py-2 text-sm focus:border-rose-600 focus:outline-none focus:ring-1 focus:ring-rose-600" />
          {err && <div data-testid="delete-password-error" className="border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-600">{err}</div>}
          <div className="flex justify-end gap-2">
            <button data-testid="delete-password-cancel" type="button" onClick={onClose} className="rounded-sm border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50">Cancel</button>
            <button data-testid="delete-password-submit" type="submit" disabled={busy || !pw}
              className="rounded-sm bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-50">
              {busy ? "Verifying…" : submitLabel}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function ConfirmDelete({ open, onClose, onConfirm, count, all, items, itemLabel = "item", warning }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setPw(""); } }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await onConfirm(pw); } finally { setBusy(false); }
  };

  const noun = count === 1 ? itemLabel : `${itemLabel}s`;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md rounded-sm" data-testid="confirm-delete-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base font-medium text-rose-700">
            <ShieldAlert className="h-4 w-4" strokeWidth={1.5} /> {all ? `Delete ALL ${itemLabel}s?` : `Delete ${count} ${noun}?`}
          </DialogTitle>
          <DialogDescription className="sr-only">Confirm and re-enter your password to permanently delete the selected items.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700">
            <p className="font-semibold">This cannot be undone.</p>
            <p className="mt-1">{warning || "Any downstream item tagged to a deleted entry will show '(deleted)' until you re-tag it."}</p>
          </div>
          {items && items.length > 0 && (
            <div className="max-h-40 overflow-auto rounded-sm border border-zinc-200 bg-zinc-50 p-2 text-xs" data-testid="delete-items-list">
              {items.slice(0, 20).map((r) => (
                <div key={r.id} className="flex items-center justify-between gap-2 border-b border-zinc-100 py-0.5 last:border-b-0">
                  <span className="font-mono text-zinc-500">{r.sku || "—"}</span>
                  <span className="flex-1 truncate text-zinc-700">{r.name}</span>
                  {typeof r.usage_count === "number" && r.usage_count > 0 && (
                    <span className="rounded-sm bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">used in {r.usage_count}</span>
                  )}
                </div>
              ))}
              {items.length > 20 && <div className="pt-1 text-zinc-500">…and {items.length - 20} more</div>}
            </div>
          )}
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.05em] text-zinc-600">Re-enter admin password to confirm</label>
            <input data-testid="confirm-delete-password-input" type="password" autoFocus value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Admin password"
              className="mt-1 block w-full rounded-sm border border-zinc-300 px-3 py-2 text-sm focus:border-rose-600 focus:outline-none focus:ring-1 focus:ring-rose-600" />
          </div>
          <div className="flex justify-end gap-2">
            <button data-testid="confirm-delete-cancel" type="button" onClick={onClose} className="rounded-sm border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50">Cancel</button>
            <button data-testid="confirm-delete-button" type="submit" disabled={busy || !pw}
              className="rounded-sm bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-50">
              {busy ? "Deleting…" : all ? `Delete ALL` : `Delete ${count}`}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
